import { Button } from '@chiroup/components/Button';
import { LoadingPage } from '@chiroup/components/LoadingPage';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Language } from '../common/verbiage';
import userService from '../services/user.service';
import LoginContainer from './common/LoginContainer';

// Public, no-auth email-subscription page. Mounted at both:
//   - `/survey/unsubscribe` (review-link + 30-day-survey SES templates)
//   - `/email-subscription` (appointment-reminder template in
//     `createNotificationEmail.ts`)
// Both URL shapes carry the same `?token=…&lang=es?` payload and
// round-trip to the legacy `/subscription-status` endpoints, which
// toggle `Patient.optOutEmail` for the email in the token.

const verbiage = {
  title: {
    en: 'Email subscription',
    es: 'Suscripción de correo electrónico',
  },
  subscribed: {
    en: 'You are currently subscribed to survey and reminder emails.',
    es: 'Actualmente está suscrito a los correos electrónicos de encuestas y recordatorios.',
  },
  unsubscribed: {
    en: 'You have been unsubscribed from survey and reminder emails.',
    es: 'Se ha dado de baja de los correos electrónicos de encuestas y recordatorios.',
  },
  unsubscribeAction: {
    en: 'Unsubscribe',
    es: 'Cancelar suscripción',
  },
  resubscribeAction: {
    en: 'Resubscribe',
    es: 'Resuscribirse',
  },
  missingToken: {
    en: 'This unsubscribe link is invalid or has expired. Please use the link from your email.',
    es: 'Este enlace para cancelar la suscripción no es válido o ha caducado. Utilice el enlace de su correo electrónico.',
  },
  errorStatus: {
    en: 'Unable to load your subscription status. Please try again.',
    es: 'No se pudo cargar el estado de su suscripción. Por favor, inténtelo de nuevo.',
  },
  errorUpdate: {
    en: 'Unable to update your subscription. Please try again.',
    es: 'No se pudo actualizar su suscripción. Por favor, inténtelo de nuevo.',
  },
} satisfies Record<string, Record<Language, string>>;

const SurveyUnsubscribe: React.FC = () => {
  const { search } = useLocation();
  const { token, lang } = qs.parse(search);
  const language: Language = lang === 'es' ? 'es' : 'en';
  const tokenStr = typeof token === 'string' ? token : '';

  const [status, setStatus] = useState<'loading' | 'ready' | 'invalid' | 'error'>(
    tokenStr ? 'loading' : 'invalid'
  );
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!tokenStr) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await userService.subscriptionStatus(tokenStr);

        if (cancelled) return;

        setSubscribed(!!res?.subscribed);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;

        console.error('subscriptionStatus failed', err);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tokenStr]);

  const toggle = async () => {
    if (!tokenStr || subscribed === null) return;

    setSubmitting(true);
    setErrorMessage(null);

    const action = subscribed ? 'unsubscribe' : 'subscribe';

    try {
      await userService.updateSubscription(tokenStr, action);
      setSubscribed(action === 'subscribe');
    } catch (err) {
      console.error('updateSubscription failed', err);
      setErrorMessage(verbiage.errorUpdate[language]);
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return <LoadingPage />;
  }

  return (
    <LoginContainer width="sm:max-w-xl">
      <div className="safe-area text-center">
        <h2 className="text-2xl font-extrabold text-gray-900">{verbiage.title[language]}</h2>

        {status === 'invalid' && (
          <div className="mt-6 rounded-md bg-red-50 p-4 text-left">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 shrink-0 text-red-400" aria-hidden="true" />
              <p className="ml-3 text-sm text-red-700">{verbiage.missingToken[language]}</p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="mt-6 rounded-md bg-red-50 p-4 text-left">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 shrink-0 text-red-400" aria-hidden="true" />
              <p className="ml-3 text-sm text-red-700">{verbiage.errorStatus[language]}</p>
            </div>
          </div>
        )}

        {status === 'ready' && (
          <>
            <p className="mt-6 text-gray-600">
              {subscribed ? verbiage.subscribed[language] : verbiage.unsubscribed[language]}
            </p>

            {!subscribed && (
              <div className="mt-4 flex items-center justify-center text-green-600">
                <CheckCircleIcon className="h-6 w-6 mr-2" aria-hidden="true" />
              </div>
            )}

            <Button
              className="mt-8"
              onClick={toggle}
              text={
                subscribed
                  ? verbiage.unsubscribeAction[language]
                  : verbiage.resubscribeAction[language]
              }
              loading={submitting}
            />

            {errorMessage && (
              <div className="mt-4 rounded-md bg-red-50 p-4 text-left">
                <div className="flex">
                  <XCircleIcon className="h-5 w-5 shrink-0 text-red-400" aria-hidden="true" />
                  <p className="ml-3 text-sm text-red-700">{errorMessage}</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </LoginContainer>
  );
};

export default SurveyUnsubscribe;
